import React from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, BellOff, Bell, CheckCheck } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import UnifiedHeader from '@/components/layout/UnifiedHeader';
import { useCurrentNavItem } from '@/hooks/useCurrentNavItem';

interface NotificationsHeaderProps {
  unreadCount: number;
  onMarkAllAsRead: () => void;
  onDeleteAll: () => void;
  notificationsEnabled: boolean;
  toggleNotifications: () => void;
}

const NotificationsHeader: React.FC<NotificationsHeaderProps> = ({
  unreadCount,
  onMarkAllAsRead,
  onDeleteAll,
  notificationsEnabled,
  toggleNotifications
}) => {
  const isMobile = useIsMobile();
  const currentNavItem = useCurrentNavItem();
  const Icon = currentNavItem?.icon || Bell;
  
  const rightContent = (
    <div className="flex gap-2">
      {/* Toggle notifications on/off */}
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 rounded-full border ${
          notificationsEnabled 
            ? "bg-green-500/20 hover:bg-green-500/30 border-green-500/50" 
            : "bg-red-500/20 hover:bg-red-500/30 border-red-500/50"
        }`}
        onClick={toggleNotifications}
        title={notificationsEnabled ? "Desativar notificações" : "Ativar notificações"}
      >
        {notificationsEnabled ? (
          <Bell className="h-4 w-4 text-green-400" />
        ) : (
          <BellOff className="h-4 w-4 text-red-400" />
        )}
      </Button>
      
      {/* Mark all as read - only show if there are unread notifications */}
      {unreadCount > 0 && (
        <Button 
          variant="ghost"
          size={isMobile ? "icon" : "sm"}
          onClick={onMarkAllAsRead}
          className="h-8 rounded-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30"
          title="Marcar todas como lidas"
        >
          <CheckCheck className="h-4 w-4 text-blue-400" />
          {!isMobile && <span className="ml-1 text-blue-400 text-xs">Lidas</span>}
        </Button>
      )}
      
      {/* Delete all notifications */}
      <Button 
        variant="ghost"
        size={isMobile ? "icon" : "sm"}
        onClick={onDeleteAll}
        className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30"
        title="Apagar todas as notificações"
      >
        <Trash2 className="h-4 w-4 text-red-400" />
        {!isMobile && <span className="ml-1 text-red-400 text-xs">Apagar</span>}
      </Button>
    </div>
  );

  return (
    <UnifiedHeader
      title={currentNavItem?.label || "Notificações"}
      subtitle={unreadCount > 0 ? `${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}` : currentNavItem?.description}
      icon={Icon}
      iconColor="text-white"
      iconBgColor="bg-luxury-pinkBright border-luxury-pinkBright"
      rightContent={rightContent}
    />
  );
};

export default NotificationsHeader;
